import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { cn } from '../../lib/utils';

/** Bilingual help text shown in an {@link InfoHint}. */
export interface BilingualHint {
  /** English text. */
  en: string;
  /** Bangla (বাংলা) text. */
  bn: string;
}

interface InfoHintProps {
  /** English help text. */
  en: string;
  /** Bangla help text. */
  bn: string;
  /** Accessible name / hover title, usually the field label. */
  label?: string;
  /** Extra classes applied to the trigger icon. */
  className?: string;
}

const TOOLTIP_WIDTH = 320;
/** Rough max height used to decide whether the tooltip flips above the icon. */
const TOOLTIP_MAX_HEIGHT = 132;

/**
 * Small info icon that reveals a bilingual (English + Bangla) help bubble.
 *
 * Renders as a `span[role=button]` rather than a real button so it can be
 * nested inside interactive rows (e.g. the policy `Switch` buttons) without
 * producing invalid HTML, and stops click propagation so it never triggers the
 * parent control. The bubble is rendered through a portal to avoid being
 * clipped by scroll containers such as modal bodies.
 */
export function InfoHint({ en, bn, label, className }: InfoHintProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;

    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - TOOLTIP_WIDTH - 8));
    let top = rect.bottom + 8;
    if (top + TOOLTIP_MAX_HEIGHT > window.innerHeight - 8) {
      top = rect.top - TOOLTIP_MAX_HEIGHT - 8;
    }
    setPos({ top: Math.max(8, top), left });
  }, []);

  const show = useCallback(() => {
    place();
    setOpen(true);
  }, [place]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    // Close on scroll/resize so the bubble never detaches from its anchor.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  return (
    <>
      <span
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-label={label ? `${label} — help` : 'Help'}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={close}
        onFocus={show}
        onBlur={close}
        onClick={(event) => {
          // Focus already reveals the bubble; just keep the click from
          // reaching a parent control (e.g. the policy Switch button).
          event.preventDefault();
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            setOpen((prev) => !prev);
          }
        }}
        className={cn(
          'inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full align-middle text-text-muted transition-colors hover:bg-primary/10 hover:text-primary',
          className,
        )}
      >
        <Info size={12} />
      </span>
      {open &&
        createPortal(
          <div
            role="tooltip"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: TOOLTIP_WIDTH }}
            className="z-[80] rounded-lg border border-border bg-surface p-3 shadow-xl"
          >
            <p className="text-xs leading-relaxed text-text">{en}</p>
            <div className="my-2 h-px bg-border" />
            <p lang="bn" className="text-xs leading-relaxed text-text-muted">
              {bn}
            </p>
          </div>,
          document.body,
        )}
    </>
  );
}
